import React, { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Printer } from "lucide-react";
import { impaginaA4, CSS_FOGLI_A4 } from "@/lib/fogliA4";
import { layoutPreventivo, aziendaPerStampa } from "@/lib/preventivoLayout";
import { HeaderAzienda } from "@/components/PreventivoHeader";

const TITOLI = {
  computo: ["Computo metrico senza prezzi", "Richiesta di preventivo: indicate prezzo unitario e totale per ogni voce."],
  materiali: ["Scheda scelta materiali", "Materiali e finiture scelti dal cliente per le voci del preventivo."],
};

// Stampe di commessa in fogli A4 (stesso motore del preventivo): computo senza prezzi o scheda materiali.
export default function CommessaStampa() {
  const { cid, tipo } = useParams();
  const [qs] = useSearchParams();
  const [wf, setWf] = useState(null);
  const [azienda, setAzienda] = useState({});
  const srcRef = useRef(null);
  const outRef = useRef(null);
  useEffect(() => {
    Promise.all([api.get(`/commesse/${cid}/workflow`), api.get("/dati-azienda").catch(() => ({ data: {} })), api.get("/impostazioni").catch(() => ({ data: {} }))])
      .then(([w, a, i]) => { setWf(w.data); setAzienda(aziendaPerStampa(a.data || {}, i.data || {})); });
  }, [cid]);
  useEffect(() => {
    if (!wf || !srcRef.current) return;
    (async () => { try { await document.fonts?.ready; } catch (_) { /* ok */ } impaginaA4(srcRef.current, outRef.current); })();
  }, [wf, azienda]);
  if (!wf) return <div className="p-12 text-center text-zinc-500">Caricamento…</div>;
  const ids = (qs.get("ids") || "").split(",").filter(Boolean);
  const all = wf.computo_metrico?.items || [];
  const items = tipo === "materiali"
    ? all.filter((it) => (it.da_scegliere == null ? it.tipo_costo === "fornitura" : it.da_scegliere))
    : all.filter((it) => !ids.length || ids.includes(it.id));
  const c = wf.commessa || {};
  const L = layoutPreventivo(azienda.layout);
  const [titolo, sotto] = TITOLI[tipo] || TITOLI.computo;
  const colore = azienda.colore_primario || "#0F172A";
  return (
    <div className="min-h-screen bg-zinc-200">
      <div className="sticky top-0 z-10 bg-zinc-900 text-white px-6 py-3 flex items-center justify-between print:hidden">
        <span className="text-xs mono uppercase tracking-widest">{titolo} · {c.numero}</span>
        <Button size="sm" onClick={() => window.print()} style={{ background: "var(--brand)", color: "white" }} data-testid="cs-stampa"><Printer className="h-4 w-4 mr-1" />Stampa / salva PDF</Button>
      </div>
      <div ref={srcRef} aria-hidden="true" className="bg-white" style={{ position: "absolute", left: "-10000px", top: 0, width: "210mm", visibility: "hidden" }}>
        <HeaderAzienda azienda={azienda} L={L} colorePrimario={colore} />
        <div className="px-12 py-5 bg-zinc-50">
          <h1 className="text-2xl font-bold" style={{ fontFamily: "Outfit", color: colore }}>{titolo}</h1>
          <div className="text-xs text-zinc-600 mt-1">{sotto}</div>
          <div className="text-xs text-zinc-700 mt-2">Commessa <b>{c.numero}</b> · Cliente <b>{c.cliente?.nome} {c.cliente?.cognome}</b>{c.cliente?.indirizzo ? ` · ${c.cliente.indirizzo}` : ""}</div>
        </div>
        <div className="px-12 py-5" data-pg="split">
          <table className="w-full text-xs border border-zinc-300" data-pg="split">
            <thead className="bg-zinc-100 text-[10px] uppercase tracking-widest text-zinc-600"><tr>
              <th className="text-left px-2 py-1.5 w-8">#</th><th className="text-left px-2">Voce</th><th className="text-right px-2 w-20">Q.tà</th>
              {tipo === "materiali" ? (<><th className="text-left px-2 w-56">Materiale scelto</th><th className="text-left px-2 w-40">Note</th></>) : (<><th className="text-right px-2 w-28">Prezzo unit.</th><th className="text-right px-2 w-28">Totale</th></>)}
            </tr></thead>
            <tbody data-pg="split">
              {items.map((it, i) => (
                <tr key={it.id} className="border-t border-zinc-200">
                  <td className="px-2 py-2 text-zinc-500">{i + 1}</td>
                  <td className="px-2 py-2"><div className="font-medium">{it.name}</div>{it.category && <div className="text-[10px] text-zinc-500">{it.category}</div>}</td>
                  <td className="px-2 text-right mono">{it.qty} {it.unit}</td>
                  {tipo === "materiali" ? (<><td className="px-2">{it.materiale_scelto || "________________"}</td><td className="px-2 text-zinc-600">{it.materiale_note}</td></>) : (<><td className="px-2 border-l border-zinc-200" /><td className="px-2 border-l border-zinc-200" /></>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-12 py-6 grid grid-cols-2 gap-12 text-xs" data-pg="firma">
          <div><div className="border-b-2 border-zinc-300 h-12" /><div className="text-zinc-500 mt-1">{tipo === "materiali" ? "Il Cliente, per approvazione" : "Timbro e firma del fornitore"}</div></div>
          <div><div className="border-b-2 border-zinc-300 h-12" /><div className="text-zinc-500 mt-1">Per {azienda.ragione_sociale || azienda.nome || "l'azienda"}</div></div>
        </div>
      </div>
      <div id="print-area" ref={outRef} className="a4-pagine" data-testid="cs-fogli" />
      <style>{CSS_FOGLI_A4 + `@media print { @page { size: A4; margin: 0; } body { background: white !important; } .a4-pagine { display: block !important; padding: 0 !important; } .a4-pagina { box-shadow: none !important; break-after: page; } .print\\:hidden { display: none !important; } }`}</style>
    </div>
  );
}
